import { GetGoChargeMapUrl } from "@/common/CommonConstants";
import { MonthlyAverageMetric } from "@/models/goFurther";
import { getJSTCurrentTime } from "@/util/datetime";

export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

export const convertStreetViewUrl = (streetViewUrl: string): string => {
  if (!streetViewUrl || !GOOGLE_MAPS_API_KEY.trim()) return "";

  try {
    const url = new URL(streetViewUrl, window.location.origin);
    const viewpoint = url.searchParams.get("viewpoint");
    const heading = url.searchParams.get("heading");

    const newUrl = new URL("https://www.google.com/maps/embed/v1/streetview");
    if (viewpoint) {
      newUrl.searchParams.set("location", viewpoint);
    }
    if (heading) {
      newUrl.searchParams.set("heading", heading);
    }
    newUrl.searchParams.set("key", GOOGLE_MAPS_API_KEY);

    return newUrl.toString();
  } catch (error) {
    console.warn("Invalid streetViewUrl", { streetViewUrl, error });
    return "";
  }
};

export const buildGoogleMapUrl = (lat: number, lng: number): string => {
  return `https://www.google.com/maps/place/${lat},${lng}/@${lat},${lng},18z`;
};

export const buildGoChargeUrl = (lat: number, lng: number): string => {
  const goChargeUrl = GetGoChargeMapUrl();
  if (!goChargeUrl) return "";

  return `${goChargeUrl}?lat=${lat}&lng=${lng}&zoom=14`;
};

export type ComparisonDataItem = {
  month: string;
  evRevenue?: number | null;
  evMileage?: number | null;
  evMileageBeforeExclusion?: number | null;
  lpgRevenue?: number | null;
  lpgMileage?: number | null;
};

export type VehicleChartDataItem = {
  month: string;
  officeLpgMileage?: number | null;
  officeLpgRevenue?: number | null;
} & Record<`v_${string}_${"mileage" | "revenue"}`, number | null | undefined>;

export const processComparisonData = (averages: MonthlyAverageMetric[] | null | undefined): ComparisonDataItem[] => {
  if (!averages) return [];

  const grouped = new Map<string, ComparisonDataItem>();

  averages.forEach((item) => {
    if (!item.yyyymm) return;

    if (!grouped.has(item.yyyymm)) {
      grouped.set(item.yyyymm, { month: item.yyyymm });
    }
    const existing = grouped.get(item.yyyymm)!;
    if (item.isEv === true) {
      existing.evRevenue = item.avgRevenue;
      existing.evMileage = item.avgMileageKm;
      existing.evMileageBeforeExclusion = item.avgMileageKmBeforeExclusion;
    } else if (item.isEv === false) {
      existing.lpgRevenue = item.avgRevenue;
      existing.lpgMileage = item.avgMileageKm;
    }
  });

  return Array.from(grouped.values()).sort((a, b) => a.month.localeCompare(b.month));
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "-";
  return Math.round(value).toLocaleString("ja-JP");
};

export const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "-";
  return `¥${Math.round(value).toLocaleString("ja-JP")}`;
};

export const formatPercent = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "-";
  return `${Math.round(value * 100)}%`;
};

const MAN_EN = 10000;

export const formatMileageAxisTick = (value: number | string): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";
  return `${Math.round(numericValue).toLocaleString("ja-JP")}km`;
};

export const formatRevenueAxisTick = (value: number | string): string => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "-";

  const roundedValue = Math.round(numericValue);
  if (Math.abs(roundedValue) >= MAN_EN) {
    const manEnValue = Math.round(roundedValue / MAN_EN);
    return `${manEnValue.toLocaleString("ja-JP")}万円`;
  }

  return `${roundedValue.toLocaleString("ja-JP")}円`;
};

export const formatPercentAxisTick = (value: number | string): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "-";
  return `${Math.round(num)}%`;
};

export const computePreviousMonthKey = (): string => {
  const currentJstDate = getJSTCurrentTime("date");
  const [year, month] = currentJstDate.split("-").map(Number);
  const date = new Date(year, month - 1, 1);
  date.setMonth(date.getMonth() - 1);
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`;
};
